import { useEffect, useRef, useState } from "react";
import { Close, Alert as AlertIcon } from "./Icons";
import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../api/client";
import type { DeclareIncidentRequest, DeclareIncidentResponse } from "../api/types";

interface ResultBanner {
  cls: "success" | "error" | "demo";
  html: string;
}

export function DeclareIncidentModal() {
  const ui = useUI();
  const data = useData();
  const auth = useAuth();

  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<DeclareIncidentRequest["severity"]>("SEV2");
  const [service, setService] = useState("");
  const [summary, setSummary] = useState("");
  const [pageOncall, setPageOncall] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<ResultBanner | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset on close
  useEffect(() => {
    if (!ui.declareOpen) {
      setBanner(null);
      setTitle(""); setSummary(""); setService("");
      setSeverity("SEV2"); setPageOncall(true); setSubmitting(false);
    }
  }, [ui.declareOpen]);

  // Focus management: focus the first field on open, restore focus to the trigger on close.
  useEffect(() => {
    if (!ui.declareOpen) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    titleRef.current?.focus();
    return () => prevFocus?.focus?.();
  }, [ui.declareOpen]);

  // Defense-in-depth: even though the trigger button and `i` shortcut are role-gated,
  // never render the declare form for a user who lacks the capability.
  if (!ui.declareOpen || !auth.canDeclare) return null;

  // Keep Tab focus inside the dialog (minimal focus trap; Esc is handled globally in Layout).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const f = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (f.length === 0) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // Service options sorted alphabetically across backend + surfaces
  const services = [
    ...(data.commandCenter?.estates.backend ?? []),
    ...(data.commandCenter?.estates.surfaces ?? []),
  ].slice().sort((a, b) => a.name.localeCompare(b.name));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setBanner(null);
    try {
      const r = await api.post<DeclareIncidentResponse>("/command-center/incidents/declare", {
        json: {
          title, severity, summary, service,
          // Identity comes from the Keycloak session, not free text — the API should
          // still re-derive it from the bearer token as the authoritative record.
          declared_by: auth.currentUser ?? "staff",
          page_oncall: pageOncall,
        },
      });
      if (r.demo_mode) {
        setBanner({
          cls: "demo",
          html: `<strong>Demo mode</strong> — command-center-api has no Slack/Jira/PagerDuty creds set, so no external side effects ran. <code>${r.incident_id}</code> would have been created.`,
        });
      } else {
        const bits = [`<strong>${r.incident_id} declared.</strong>`];
        if (r.slack_channel) bits.push(` Slack: <code>${r.slack_channel}</code>.`);
        if (r.jira_key)      bits.push(` Jira: <code>${r.jira_key}</code>.`);
        if (r.pagerduty_id)  bits.push(` PagerDuty paged.`);
        setBanner({ cls: "success", html: bits.join("") });
      }
    } catch (err) {
      const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
      setBanner({ cls: "error", html: `<strong>Failed:</strong> ${msg}. Is command-center-api running?` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay open"
         onClick={(e) => {
           if ((e.target as HTMLElement).classList.contains("modal-overlay")) ui.setDeclareOpen(false);
         }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="declare-title"
           ref={dialogRef} onKeyDown={onKeyDown}>
        <div className="modal-head">
          <h3 id="declare-title">Declare incident</h3>
          <button className="icon-btn" aria-label="Close" onClick={() => ui.setDeclareOpen(false)}>
            <Close size={14} />
          </button>
        </div>
        <p className="modal-sub">
          Creates a Slack channel, files a Jira ticket, and pages on-call.{" "}
          <strong>Reversible only by manual cleanup</strong> — declare only when the symptom is real.
        </p>
        <form className="modal-body" onSubmit={onSubmit}>
          <label>
            Title <span className="req" aria-hidden="true">*</span> <span className="hint">short, what is breaking?</span>
            <input ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)}
                   required minLength={4} maxLength={140}
                   placeholder="e.g. payment-action returning 503 on /v2/payments"
                   autoComplete="off" />
          </label>
          <div className="modal-row">
            <label>
              Severity
              <select value={severity} onChange={(e) => setSeverity(e.target.value as DeclareIncidentRequest["severity"])}>
                <option value="SEV1">SEV1 — customer impact, P0 service down</option>
                <option value="SEV2">SEV2 — partial impact / degradation</option>
                <option value="SEV3">SEV3 — internal / no customer impact</option>
              </select>
            </label>
            <label>
              Affected service
              <select value={service} onChange={(e) => setService(e.target.value)}>
                <option value="">— choose later —</option>
                {services.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name} {s.tier ? `(${s.tier})` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Summary <span className="hint">what symptom you saw, where</span>
            <textarea rows={3} maxLength={2000} value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      placeholder="Loki shows 503 spike since 14:32 UTC; alert DuplicateKeyINITIATED firing in #waffy-alerts-prod" />
          </label>
          <div className="modal-row">
            <label>
              Declared by <span className="hint">from your Keycloak session</span>
              <div className="identity-field" title="Derived from your signed-in identity">
                <span className="identity-dot" aria-hidden="true" />
                {auth.currentUser}
              </div>
            </label>
            <label className="modal-checkbox">
              <input type="checkbox" checked={pageOncall}
                     onChange={(e) => setPageOncall(e.target.checked)} />
              <span>Page on-call (PagerDuty)</span>
            </label>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn-ghost" onClick={() => ui.setDeclareOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              <AlertIcon size={14} />
              {submitting ? "Declaring…" : "Declare"}
            </button>
          </div>
          {banner && (
            <div className={`declare-result ${banner.cls}`}
                 role="status" aria-live="polite"
                 dangerouslySetInnerHTML={{ __html: banner.html }} />
          )}
        </form>
      </div>
    </div>
  );
}
